import React, { useEffect, useState } from 'react';
import { Form, Input, message, DatePicker, Radio, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import * as accountService from '../../services/account/accountService';

const AccountApprovalDrawer = ({ visible, account, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [approvalAction, setApprovalAction] = useState('approve'); // 'approve' or 'reject'

  useEffect(() => {
    if (visible && account) {
      form.resetFields();
      setApprovalAction('approve');
    }
  }, [visible, account, form]);

  const handleApprovalActionChange = (e) => {
    setApprovalAction(e.target.value);
    if (e.target.value === 'reject') {
      form.setFieldValue('expiryDate', null);
    }
  };
  

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      if (approvalAction === 'approve') {
        // 审批通过
        const response = await accountService.approveAccount({
          loginId: account.loginId,
          expiryDate: values.expiryDate ? dayjs(values.expiryDate).format('YYYY-MM-DD') : null,
        });
        
        if (response.success) {
          message.success(t('account.approval.approveSuccess'));
          onSuccess();
        } else {
          message.error(response.errorMessage || t('account.approval.approveFailed'));
        }
      } else {
        // 拒绝申请
        if (!values.rejectReason) {
          message.error(t('account.approval.rejectReasonRequired'));
          return;
        }
        const response = await accountService.rejectAccount({
          loginId: account.loginId,
          rejectReason: values.rejectReason,
        });
        
        if (response.success) {
          message.success(t('account.approval.rejectSuccess'));
          onSuccess();
        } else {
          message.error(response.errorMessage || t('account.approval.rejectFailed'));
        }
      }
    } catch (error) {
      console.error('Approval failed:', error);
      message.error(t('account.approval.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      onOk={handleSubmit}
      title={t('account.approval.title')}
      confirmLoading={loading}
      width={480}
      maskClosable={false}
      destroyOnClose={true}
      okText={t('common.confirm')}
      cancelText={t('common.cancel')}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 24 }}
      >
        <Form.Item label={t('account.approval.action')} required>
          <Radio.Group 
            value={approvalAction} 
            onChange={handleApprovalActionChange}
            buttonStyle="solid"
          >
            <Radio.Button value="approve">{t('account.approval.approve')}</Radio.Button>
            <Radio.Button value="reject" danger>{t('account.approval.reject')}</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {approvalAction === 'approve' ? (
          <Form.Item
            name="expiryDate"
            label={<span className="required-label">{t('account.expiryDate')}</span>}
            rules={[{ required: true, message: t('account.expiryDateRequired') }]}
            extra={t('account.approval.expiryDateHint')}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder={t('common.pleaseSelect')}
              disabledDate={(current) => current && current < dayjs().startOf('day')}
            />
          </Form.Item>
        ) : (
          <Form.Item
            name="rejectReason"
            label={<span className="required-label">{t('account.approval.rejectReason')}</span>}
            rules={[{ required: true, message: t('account.approval.rejectReasonRequired') }]}
          >
            <Input.TextArea
              rows={4}
              placeholder={t('account.approval.rejectReasonPlaceholder')}
              maxLength={200}
              showCount
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default AccountApprovalDrawer;
